import { useEffect, useState } from "react";
import * as eventService from "../../services/eventService";

type Props = {
  open: boolean;
  onClose: () => void;
  onCreated: (e: eventService.EventoAdmin) => void;
};

const DEFAULT_DATE = "2027-08-20"; // YYYY-MM-DD
const DEFAULT_TIME_UTC = "20:00:00Z"; // hora fija para armar el ISO

function toIsoFromDate(dateYYYYMMDD: string) {
  // "2027-08-20" -> "2027-08-20T20:00:00Z"
  return `${dateYYYYMMDD}T${DEFAULT_TIME_UTC}`;
}

function CreateEventModal({ open, onClose, onCreated }: Props) {
  const [nombre, setNombre] = useState("");
  const [slug, setSlug] = useState("");

  // UI: calendario (solo fecha)
  const [fechaPrincipalDate, setFechaPrincipalDate] = useState(DEFAULT_DATE);
  const [fechaLimiteRsvpDate, setFechaLimiteRsvpDate] = useState(DEFAULT_DATE);

  // API: ISO (se arma al enviar)
  const [zonaHoraria, setZonaHoraria] = useState("America/Lima");

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Cuando abre el modal, resetea defaults si quieres
  useEffect(() => {
    if (!open) return;
    setErrorMsg(null);
  }, [open]);

  if (!open) return null;

  async function crear(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setErrorMsg(null);

    try {
      const fechaPrincipal = toIsoFromDate(fechaPrincipalDate);
      const fechaLimiteRsvp = toIsoFromDate(fechaLimiteRsvpDate);

      const created = await eventService.crearEvento({
        nombre,
        slug,
        fechaPrincipal,
        fechaLimiteRsvp,
        zonaHoraria,
      });

      onCreated(created);

      // reset
      setNombre("");
      setSlug("");
      setFechaPrincipalDate(DEFAULT_DATE);
      setFechaLimiteRsvpDate(DEFAULT_DATE);
      setZonaHoraria("America/Lima");
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : "Error al crear evento");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        className="absolute inset-0 bg-black/60 backdrop-blur-[2px]"
        onClick={() => {
          if (!loading) onClose();
        }}
        aria-label="Cerrar"
      />

      <div className="relative w-full max-w-[620px] rounded-2xl border border-white/10 bg-surface-darker shadow-2xl overflow-hidden">
        <div className="px-6 py-5 border-b border-white/10 flex items-center justify-between">
          <div>
            <h3 className="text-xl font-serif text-white">Crear evento</h3>
            <p className="text-sm text-slate-400">Registra un nuevo evento.</p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="p-2 rounded-lg text-slate-300 hover:text-white hover:bg-white/5 transition-colors"
            aria-label="Cerrar modal"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        <form onSubmit={crear} className="px-6 py-6 space-y-5">
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-white">Nombre</label>
            <input
              className="w-full rounded-lg border border-white/10 bg-background-dark text-white placeholder-slate-500 focus:border-primary focus:ring-1 focus:ring-primary h-11 px-4 transition-all duration-200"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="Ej: Matrimonio X & Y"
              required
              disabled={loading}
            />
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-white">Slug</label>
            <input
              className="w-full rounded-lg border border-white/10 bg-background-dark text-white placeholder-slate-500 focus:border-primary focus:ring-1 focus:ring-primary h-11 px-4 transition-all duration-200"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="Ej: x-y-2027"
              required
              disabled={loading}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-white">Fecha principal</label>
              <input
                type="date"
                className="w-full rounded-lg border border-white/10 bg-background-dark text-white placeholder-slate-500 focus:border-primary focus:ring-1 focus:ring-primary h-11 px-4 transition-all duration-200"
                value={fechaPrincipalDate}
                onChange={(e) => setFechaPrincipalDate(e.target.value)}
                required
                disabled={loading}
              />
              <p className="text-xs text-slate-400">
                Se guardará como:{" "}
                <span className="text-slate-200">{toIsoFromDate(fechaPrincipalDate)}</span>
              </p>
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-white">Fecha límite RSVP</label>
              <input
                type="date"
                className="w-full rounded-lg border border-white/10 bg-background-dark text-white placeholder-slate-500 focus:border-primary focus:ring-1 focus:ring-primary h-11 px-4 transition-all duration-200"
                value={fechaLimiteRsvpDate}
                onChange={(e) => setFechaLimiteRsvpDate(e.target.value)}
                required
                disabled={loading}
              />
              <p className="text-xs text-slate-400">
                Se guardará como:{" "}
                <span className="text-slate-200">{toIsoFromDate(fechaLimiteRsvpDate)}</span>
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-white">Zona horaria</label>
              <input
                className="w-full rounded-lg border border-white/10 bg-background-dark text-white placeholder-slate-500 focus:border-primary focus:ring-1 focus:ring-primary h-11 px-4 transition-all duration-200"
                value={zonaHoraria}
                onChange={(e) => setZonaHoraria(e.target.value)}
                placeholder="America/Lima"
                required
                disabled={loading}
              />
          </div>

          {errorMsg && <p className="text-sm text-red-300">{errorMsg}</p>}

          <div className="pt-2 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 h-11 rounded-lg border border-white/10 bg-white/5 text-white hover:bg-white/10 transition-colors"
            >
              Cancelar
            </button>

            <button
              type="submit"
              disabled={loading}
              className="px-5 h-11 rounded-lg bg-primary hover:bg-primary/90 text-white font-semibold transition-all shadow-lg shadow-primary/20 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <span className="inline-flex items-center justify-center gap-2">
                {loading && (
                  <span className="material-symbols-outlined text-[18px] animate-spin">progress_activity</span>
                )}
                {loading ? "Creando..." : "Crear"}
              </span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateEventModal;
